import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { FormService } from 'src/app/data/service/form.service';
import { MagirusBalmungService } from 'src/app/data/service/magirus-balmung.service';
import { AuthService } from 'src/app/data/service/auth.service';
import { environment } from 'src/environments/environment';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-magirus-calculation-component',
  templateUrl: './magirus-calculation-component.component.html',
  styleUrls: ['./magirus-calculation-component.component.scss'],
})
export class MagirusCalculationComponentComponent implements OnInit {

  @Input() offerId?: string | null;
  @Input() filloutId?: string | null;
  @Input() inModal?: boolean = false;
  @Input() formId?: string | null;
  @Output() closed: EventEmitter<void> = new EventEmitter();

  public balmungOfferId: string | null = null;
  public loading: boolean = false;
  public error: string | null = null;
  public offerName: string | null = null;
  public items: any[] = [];
  public displayItems: any[] = [];
  public totals = {
    totalExternalPurchase: 0,
    totalHours: 0,
    coverage: 0,
  totalGross: 0,
  serverOfferSumMain: null as number | null
  };
  public optionalTotals: any = null;
  public supplierHourlyRate: number | null = null;
  public missingSupplierPositions: any[] = [];
  public includeOptionals: boolean = false; // whether to include optional items in totals
  public serverCoverage: number | null = null;

  constructor(private formService: FormService, private authService: AuthService, private magirusBalmungService: MagirusBalmungService, private modalCtrl: ModalController) { }

  ngOnInit() {
    this.loadData();
  }

  // recompute totals; includeOptionals toggles whether optional items are added into the totals
  private computeTotals(includeOptionals: boolean) {
    const itemsForTotals = this.items.filter(it => !it._missingPurchase && it.laborHoursPerUnit !== null && it.laborHoursPerUnit !== undefined && it.priceGross !== null && it.priceGross !== undefined);
    // if not including optionals, exclude optional items
    const considered = includeOptionals ? itemsForTotals : itemsForTotals.filter(it => !it.optional);

  // compute aggregates
  this.totals.totalExternalPurchase = considered.reduce((s, it) => s + (Number(it.purchasePerUnit || 0) * Number(it.amount || 0)), 0);
  this.totals.totalHours = considered.reduce((s, it) => s + (Number(it.laborHoursPerUnit || 0) * Number(it.amount || 0)), 0);
  // NOTE: Do NOT compute totals.coverage here — prefer server-provided totals.coverage. Per-item coverage is set on load.
  this.totals.totalGross = considered.reduce((s, it) => s + (Number(it.priceGross || 0) * Number(it.amount || 0)), 0);
  }

  public toggleIncludeOptionals(enabled: boolean) {
    this.includeOptionals = !!enabled;
    // recompute totals after changing toggle
    this.computeTotals(this.includeOptionals);
    // adjust coverage: if server provided coverage exists, use it as base and add optional coverage when enabled
    if (this.serverCoverage !== null) {
      if (this.includeOptionals) {
        const optionalItems = this.items.filter(it => it.optional && !it._missingPurchase && it.laborHoursPerUnit !== null && it.laborHoursPerUnit !== undefined && it.priceGross !== null && it.priceGross !== undefined);
        const optionalCoverage = optionalItems.reduce((s, it) => s + ((Number(it._deckungsbeitrag || 0) * Number(it.amount || 0))), 0);
        this.totals.coverage = Number(this.serverCoverage) + optionalCoverage;
      } else {
        this.totals.coverage = Number(this.serverCoverage);
      }
    } else {
      // fallback: compute locally based on considered items
      const itemsForTotals = this.items.filter(it => !it._missingPurchase && it.laborHoursPerUnit !== null && it.laborHoursPerUnit !== undefined && it.priceGross !== null && it.priceGross !== undefined);
      const considered = this.includeOptionals ? itemsForTotals : itemsForTotals.filter(it => !it.optional);
      this.totals.coverage = considered.reduce((s, it) => s + ((Number(it._deckungsbeitrag || 0) * Number(it.amount || 0))), 0);
    }
  }

  async loadData() {
    this.error = null;
    try {
      this.loading = true;
      // ensure we can obtain current user (await cached user) — avoid premature negative check
      let currentUser = null;
      try {
        currentUser = await this.authService.getCurrentUserCached();
      } catch (err) {
        this.error = 'Zugriff verweigert: Bitte einloggen.';
        this.loading = false;
        return;
      }
      const allowedCompanyId = environment.magirusCompanyId ?? 45;
      if (!currentUser || currentUser.companyId !== allowedCompanyId) {
        this.error = 'Zugriff verweigert: Nicht berechtigt für Magirus-Kalkulation.';
        this.loading = false;
        return;
      }

      if (this.offerId) {
        // directly show provided offerId
        this.balmungOfferId = this.offerId;
    // fetch calc data when we have an offerId
    this.loadCalcForOffer();
      } else if (this.filloutId) {
        // ask FormService for fillout and extract balmungOfferId
        if (!this.formId) {
          this.error = 'Kein formId angegeben. Bitte übergebe formId zusammen mit filloutId.';
          this.loading = false;
          return;
        }
        this.formService.getFilloutById(this.filloutId, this.formId).pipe(take(1)).subscribe({
      next: (f: any) => {
        // response typically contains .data which is the saved model
        const data = f?.data || f;
            // try several common locations
            this.balmungOfferId = data?.balmungOfferId || data?.offerId || data?.offerIdRef || data?.balmungOfferIdRef || null;
            if (!this.balmungOfferId) {
              // check nested model structures
              this.balmungOfferId = data?.model?.balmungOfferId || data?.data?.balmungOfferId || null;
            }
            this.loading = false;
            if (this.balmungOfferId) {
              this.loadCalcForOffer();
            }
          },
          error: (e) => {
            this.error = 'Fehler beim Laden des Fillouts';
            this.loading = false;
          }
        });
        return;
      }
    } catch (e) {
      this.error = 'Unbekannter Fehler';
      this.loading = false;
    }
  }

  async close() {
    if (this.inModal) {
      try {
        await this.modalCtrl.dismiss();
      } catch (e) {
        // ignore if already dismissed
      }
    } else {
      this.closed.emit();
    }
  }

  private loadCalcForOffer() {
    if (!this.balmungOfferId) return;
    this.loading = true;
    this.magirusBalmungService.getOfferCalc(this.balmungOfferId).pipe(take(1)).subscribe({
      next: (res: any) => {
        this.offerName = res?.offerName || null;
        this.supplierHourlyRate = res?.supplierHourlyRate ?? null;
        this.items = Array.isArray(res?.items) ? res.items : [];

        // mark missing purchase prices and compute per-item deckungsbeitrag
        // Requirement: per-item deckungsbeitrag = Verkaufspreis - Summe (no further math). We store per-unit value in _deckungsbeitrag
        // and use _deckungsbeitrag * amount when summing totals.
        this.items.forEach(it => {
          it._missingPurchase = it.purchasePerUnit === null || it.purchasePerUnit === undefined;
          const priceGross = Number(it.priceGross || 0);
          const totalPerPos = Number(it.totalPerPosition || 0);
          // per-unit coverage
          it._deckungsbeitrag = (priceGross - totalPerPos);
        });

        // capture server totals.offerSumMain if present, but still compute local totals for toggling
        if (res?.totals && (res.totals.offerSumMain !== undefined && res.totals.offerSumMain !== null)) {
          this.totals.serverOfferSumMain = Number(res.totals.offerSumMain);
        } else {
          this.totals.serverOfferSumMain = null;
        }

        // compute totals based on items; allow including optional items if toggled
        this.computeTotals(this.includeOptionals);

        // prefer server-provided coverage total if available
        if (res?.totals && (res.totals.coverage !== undefined && res.totals.coverage !== null)) {
          this.serverCoverage = Number(res.totals.coverage);
          // if includeOptionals is enabled, add optional coverage from items; otherwise use server value directly
          if (this.includeOptionals) {
            const optionalItems = this.items.filter(it => it.optional && !it._missingPurchase && it.laborHoursPerUnit !== null && it.laborHoursPerUnit !== undefined && it.priceGross !== null && it.priceGross !== undefined);
            const optionalCoverage = optionalItems.reduce((s, it) => s + ((Number(it._deckungsbeitrag || 0) * Number(it.amount || 0))), 0);
            this.totals.coverage = this.serverCoverage + optionalCoverage;
          } else {
            this.totals.coverage = this.serverCoverage;
          }
        } else {
          this.serverCoverage = null;
          // fallback: sum per-unit _deckungsbeitrag * amount for considered items
          const itemsForTotals = this.items.filter(it => !it._missingPurchase && it.laborHoursPerUnit !== null && it.laborHoursPerUnit !== undefined && it.priceGross !== null && it.priceGross !== undefined);
          const considered = this.includeOptionals ? itemsForTotals : itemsForTotals.filter(it => !it.optional);
          this.totals.coverage = considered.reduce((s, it) => s + ((Number(it._deckungsbeitrag || 0) * Number(it.amount || 0))), 0);
        }

        // server-provided optionalTotals (if present) — keep but also compute local optional totals
        this.optionalTotals = res?.optionalTotals ?? null;
        // compute local optional totals summary to ensure we can add these if needed
        const optItems = this.items.filter(it => it.optional);
        const optOfferSum = optItems.reduce((s, it) => s + (Number(it.priceGross || 0) * Number(it.amount || 0)), 0);
        if (!this.optionalTotals) {
          this.optionalTotals = { offerSumOptional: optOfferSum };
        } else {
          // ensure offerSumOptional exists
          this.optionalTotals.offerSumOptional = this.optionalTotals.offerSumOptional ?? optOfferSum;
        }
        this.missingSupplierPositions = Array.isArray(res?.missingSupplierPositions) ? res.missingSupplierPositions : [];

        // prepare display items: only those where core data is present
        this.displayItems = this.items.filter(it => !it._missingPurchase && it.laborHoursPerUnit !== null && it.laborHoursPerUnit !== undefined && it.priceGross !== null && it.priceGross !== undefined);
        this.loading = false;
      },
      error: (e) => {
        this.error = 'Fehler beim Laden der Kalkulation';
        this.loading = false;
      }
    });
  }

}
